import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { OverviewSectionCard } from '@/components/agent/overview/OverviewSectionCard';
import { OverviewSettingRow } from '@/components/agent/overview/OverviewSettingRow';
import { AssetConfig, TouchpointConfig, StickerSize } from '@/types/activation/activation-pack';

interface PackCustomizationSectionProps {
  assetConfig: AssetConfig;
  touchpoints: TouchpointConfig;
  isEditing?: boolean;
  onEditToggle?: () => void;
  onAssetConfigChange: (updates: Partial<AssetConfig>) => void;
  brand?: string;
  disabled?: boolean;
}

export const PackCustomizationSection = ({
  assetConfig,
  touchpoints,
  isEditing = false,
  onEditToggle,
  onAssetConfigChange,
  brand = '#4E9CFF',
  disabled = false,
}: PackCustomizationSectionProps) => {
  const showStickerSize = touchpoints.qr_sticker;

  return (
    <OverviewSectionCard
      title="Customization"
      subtitle="Configure branding and asset options"
      genieIcon="book-open"
      brand={brand}
      onEdit={disabled ? undefined : onEditToggle}
    >
      {disabled ? (
        <div className="text-sm text-gray-500 italic py-4">
          Select a target above to configure customization options
        </div>
      ) : isEditing ? (
        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="tagline">Tagline</Label>
            <Input
              id="tagline"
              value={assetConfig.tagline || ''}
              onChange={(e) => onAssetConfigChange({ tagline: e.target.value })}
              placeholder="Scan to talk to our assistant"
            />
            <p className="text-xs text-muted-foreground">
              Displayed on stickers and marketing materials
            </p>
          </div>

          {showStickerSize && (
            <div className="space-y-2">
              <Label>Sticker Size</Label>
              <Select
                value={assetConfig.sticker_size || '3x3'}
                onValueChange={(value) => onAssetConfigChange({ sticker_size: value as StickerSize })}
              >
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2x2">2" x 2"</SelectItem>
                  <SelectItem value="3x3">3" x 3"</SelectItem>
                  <SelectItem value="4x4">4" x 4"</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center space-x-3">
            <Checkbox
              id="include-logo"
              checked={assetConfig.qr_include_logo ?? true}
              onCheckedChange={(checked) => onAssetConfigChange({ qr_include_logo: !!checked })}
            />
            <Label htmlFor="include-logo">Include logo in QR codes</Label>
          </div>

          <div className="flex items-center space-x-3">
            <Checkbox
              id="include-phone"
              checked={assetConfig.include_phone_number ?? true}
              onCheckedChange={(checked) => onAssetConfigChange({ include_phone_number: !!checked })}
            />
            <Label htmlFor="include-phone">Include phone number on materials</Label>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <OverviewSettingRow
            label="Tagline"
            value={
              assetConfig.tagline ? (
                <span className="italic">"{assetConfig.tagline}"</span>
              ) : (
                <span className="text-gray-500 italic">Not set</span>
              )
            }
          />
          {showStickerSize && (
            <OverviewSettingRow
              label="Sticker Size"
              value={`${assetConfig.sticker_size || '3x3'} inches`}
            />
          )}
          <OverviewSettingRow
            label="QR Code Logo"
            value={assetConfig.qr_include_logo !== false ? 'Included' : 'Not included'}
          />
          <OverviewSettingRow
            label="Phone Number"
            value={assetConfig.include_phone_number !== false ? 'Shown on materials' : 'Hidden'}
          />
        </div>
      )}
    </OverviewSectionCard>
  );
};
